import { Link as RouterLink } from 'react-router';

import appStrings from '@/assets/constants/appStrings-no.ts';
import routes from '@/assets/constants/routes-no.ts';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/button-2.tsx';

import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_SMALL_LOAN } from './RRS.ts';

const columns = ['Renter/Lån', '100 000 kr', '300 000 kr', '500 000 kr'];
const rows = [
  {
    key: 1,
    items: ['Renteutgifter per år ved 25 %', '25 000 kr', '75 000 kr', '125 000 kr'],
  },
  {
    key: 2,
    items: ['Renteutgifter per år ved 20 %', '20 000 kr', '60 000 kr', '100 000 kr'],
  },
  {
    key: 3,
    items: ['Renteutgifter per år ved 10 %', '10 000 kr', '30 000 kr', '50 000 kr'],
  },
];

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const SmallLoan = () => {
  const { toggleChat } = useBrainSession();

  const handleRefinanceClick = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  return (
    <Page title={appStrings.MENU.SMALL_LOAN}>
      <RrsHandler {...RRS_SMALL_LOAN} />
      <Section>
        <div className="flex max-w-[900px]! flex-col [&>button]:self-center">
          <Breadcrumbs
            className="mb-8"
            items={[
              { text: appStrings.MENU.HOME, url: routes.HOME },
              {
                text: appStrings.MENU.MY_ECONOMY,
                url: routes.MY_ECONOMY,
              },
              { text: appStrings.MENU.SMALL_LOAN },
            ]}
          />
          <h1 className="mb-6 typography-v1-legacy-h2 text-[#18A0FB] sm:mb-8">
            Samle smålån og spar tusenvis – hver måned
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Har du flere dyre smålån? Når du refinansierer og samler dem til ett lån, får du raskere
            kontroll og orden på økonomien din – så enkelt gjør du det.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Det er mange som har smålån i Norge, og ifølge{' '}
            <a
              href="https://www.ssb.no/inntekt-og-forbruk/artikler-og-publikasjoner/forbruksgjelda-tynger-mest-for-dem-med-sma-ressurser"
              target="_blank"
              rel="noopener noreferrer"
              className="typography-v1-legacy-subtitle2 text-[#18A0FB]! no-underline hover:underline"
            >
              Statistisk sentralbyrå
            </a>{' '}
            har vi såkalt usikret gjeld for nærmere 150 milliarder. Dette utgjør kun 3 prosent av
            husholdningenes samlede gjeld, men andelen nordmenn over 18 år som har smålån er så mye
            som nesten 30 prosent.
            <br />
            <br />
            De skylder i gjennomsnitt 122.000 kroner, hvilket i seg selv kanskje ikke er mye penger.
            Men smålån har noen utfordringer som gjør at selv en liten sum kan vokse seg stor. En av
            dem er at istedenfor å spare, velger stadig flere å ta opp smålån for å finansiere et
            økende forbruk.
          </p>
          <Button color="yellow" onClick={handleRefinanceClick}>
            Refinanser dine smålan
          </Button>
        </div>
      </Section>
      <Section background="paper" last>
        <div className="flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row">
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Hva er smålån?</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Smålån er nemlig et annet navn på forbrukslån, altså et lån uten sikkerhet, og som
              navnet tilsier dreier det seg om mindre summer. Dette kan også være kredittkort,
              handlekontoer og avbetalinger. Det som gjerne kjennetegner denne typen lån er at
              nettopp fordi du ikke trenger å stille sikkerhet, er renten høyere enn for eksempel
              når du låner til bolig, hytte og bil.
              <br />
              <br />
              Fordelen med smålån er at siden de ikke er øremerket til et formål, kommer pengene
              raskt på konto og du kan bruke dem til hva du vil. Ulempen er at noen anser smålånene
              som ekstra lønn eller gratis penger. Da kan du fort havne i en uoversiktlig økonomisk
              situasjon, der du i verste fall ikke klarer å tilbakebetale lånene dine.
              <br />
              <br />
              Hvis dette er i ferd med å skje deg, bør du samle alle smålånene dine til ett.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Samle smålån</h3>
            <p className="typography-v1-legacy-p3">
              Det vil gi deg oversikten tilbake og en hverdag med mindre stress, fordi:
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <ul className="typography-v1-legacy-list typography-v1-legacy-p3">
              <li>Du får lavere rente</li>
              <li>Du får kun én regning i måneden</li>
              <li>Du får kun én bank å forholde deg til</li>
              <li>Du får færre gebyrer, kostnader og avgifter</li>
              <li>Du får forutsigbarhet siden du vet hva du skal betale</li>
              <li>Du får lavere totalkostnad</li>
              <li>Du får mulighet til å spare penger igjen</li>
              <li>Du får kontroll på økonomien din</li>
            </ul>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Du har med andre ord alt å vinne på å samle smålånene dine. Og det kan være mange
              penger å spare, selv om lånene ikke utgjør mye til sammen.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Samle dyre smålån – så mye sparer du
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              I tabellen under ser du hvor mye du sparer i renteutgifter hvert år når du samler
              smålånene dine.
            </p>
          </div>

          <div className="relative mb-12 w-full basis-full overflow-x-auto rounded-lg border border-[#CEE2FF] shadow-[0px_4px_8px_rgba(24,160,251,0.12)]">
            <table className="w-full border-collapse border-hidden">
              <thead>
                <tr className="align-top">
                  {columns.map((column, index) => {
                    const isFirstColumn = index === 0;
                    return (
                      <th
                        key={column}
                        scope="col"
                        className={
                          isFirstColumn
                            ? 'sticky left-0 z-10 w-[150px] min-w-[150px] border border-[#CEE2FF] bg-white px-4! py-2! text-left typography-v1-legacy-table-cell font-normal text-v1-background-200 sm:min-w-[250px]'
                            : 'min-w-[115px] border border-[#CEE2FF] px-4! py-2! text-right typography-v1-legacy-table-cell font-normal text-v1-background-200'
                        }
                      >
                        {isFirstColumn && (
                          <span
                            className="absolute top-[-8px] left-[150px] h-[243px] w-px bg-[#CEE2FF] min-[680px]:hidden sm:left-[250px] sm:h-[170px]"
                            aria-hidden
                          />
                        )}
                        {column}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ key, items }) => (
                  <tr key={key} className="align-top">
                    {items.map((text, index) => {
                      const isFirstColumn = index === 0;
                      return (
                        <td
                          key={text}
                          scope={isFirstColumn ? 'row' : undefined}
                          className={
                            isFirstColumn
                              ? 'sticky left-0 z-10 w-[150px] min-w-[150px] border border-[#CEE2FF] bg-white px-4! py-2! text-left typography-v1-legacy-table-cell font-normal text-v1-background-200 sm:min-w-[250px]'
                              : 'min-w-[115px] border border-[#CEE2FF] px-4! py-2! text-right typography-v1-legacy-table-cell text-[#18A0FB]'
                          }
                        >
                          {text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="w-full sm:w-1/2 sm:pr-6">
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Ønsker du å refinansiere 500 000 kroner og får innvilget en rente på 10 prosent,
              sparer du hele 75 000 kroner i året dersom renten du har i dag er på 25 prosent. Det
              tilsvarer 6250 kroner – hver måned! Bruker du pengene til høyere innbetalinger på
              lånet sparer du enda mer i renteutgifter. Det betyr at{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                med refinansiering kan du bli gjeldfri raskere.
              </RouterLink>
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Refinansiering av smålån med Scoopr
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              I Scoopr har vi hjulpet tusenvis av mennesker som har fått lavere renter, bedre
              betingelser og færre bekymringer – og vi kan hjelpe deg også. Hos oss kan du søke om{' '}
              <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                refinansiering fra 20.000 til 500.000 kroner
              </RouterLink>
              . Det tar deg kun to minutter, og er enkelt, gratis og uforpliktende.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <p className="typography-v1-legacy-p3">
              Når du har søkt, gjør Scoopr jobben for deg. Da sendes søknaden din automatisk til 13
              banker, som konkurrerer om å gi deg så lav rente som mulig. Når du har fått tilbudene,
              bestemmer du selv hvilket som er best for deg. Og snart kan du se frem til en hverdag
              med mer frihet og bedre økonomi.
              <br />
              <br />
              Et lite tips til slutt: Husk å vær nøye med å samle absolutt all gjeld, slik at du
              ikke finner et glemt kredittkort eller smålån på 20 000 kroner etter at du har søkt om
              refinansiering.
              <br />
              <br />
              <strong>
                Ønsker du å{' '}
                <RouterLink to={routes.PRODUCTS_REFINANCING} className={linkClassName}>
                  samle smålånene dine og refinansiere hos Scoopr
                </RouterLink>
                ? To minutters arbeid kan spare deg for tusenvis av kroner – hver måned.
              </strong>
            </p>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-center">
          <Button color="yellow" onClick={handleRefinanceClick}>
            Refinanser dine smålan
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default SmallLoan;
